import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { EditableInfoCard } from '@/components/editable-info-card';
import { SubmitProps } from '@/components/editable-info-page';
import { SyncEdlinkModal } from '@/components/edlink/sync-edlink-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FormLabel } from '@/components/ui/form';
import { useToast } from '@/components/ui/hooks/use-toast';
import { comboBoxOptionsOf } from '@/components/ui/multi-select-utils';
import { RoleBadgeList } from '@/components/users/role-badge';
import {
  dryRunSyncStudentFromEdlink,
  getUser,
  getUsers,
  syncStudentFromEdlink,
  updateCoachProfile,
  updateStudentProfile,
  updateUser,
} from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';
import { AllTimeZones, getTimezoneTypeahead } from '@/lib/time-zones';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student';
import { editBasicInfoFormSchema, type EditBasicInfoForm } from '@/routes/_layout/users/-components/form-schema';
import { AirAdminMenuItems } from '@/routes/_layout/users/-components/manage-air-admin';
import { ManageStudent } from '@/routes/_layout/users/-components/manage-student';
import { PromoteCoach } from '@/routes/_layout/users/-components/promote-coach';
import { RefreshZoomInfo } from '@/routes/_layout/users/-components/refresh-zoom-info';
import { ManageCoach } from '@/routes/_layout/users/-components/manage-coach';
import { ManageEdgeCoach } from '@/routes/_layout/users/-components/manage-edge-coach';
import { DeleteUser } from '@/routes/_layout/users/-components/delete-user';
import {
  coachGradeOptions,
  coachGradeSchema,
  isAirAdmin,
  isCoach,
  isEdgeCoach,
  isStudentRole,
} from '@airreading/common';
import { studentYearOption, studentYearSelectableSchema, studentYearValues, ZoomType } from '@airreading/common/data';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useCallback } from 'react';
import { z } from 'zod';
import { validateRouteRole } from '@/lib/routing';
import { SsoEditableInfoCard } from '@/components/sso/sso-info-card';

export const Route = createFileRoute('/_layout/users/$userId')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: UserPage,
});

const zoomTypeToDisplayName = (type?: ZoomType | null): string | undefined => {
  if (!type) return undefined;

  switch (type) {
    case 'basic':
      return 'Basic';
    case 'license':
      return 'Licensed';
    case 'permanent_license':
      return 'Permanent Licensed';
  }
};

const editCoachProfileSchema = z.object({
  isTrial: z.boolean({ required_error: 'Is trial is required' }),
  qualityGrade: coachGradeSchema.optional(),
  demoGrade: coachGradeSchema.optional(),
  behaviorNotes: z.string().optional(),
});

const editStudentProfileSchema = z.object({
  grade: studentYearSelectableSchema,
  specialEd: z.boolean().default(false).optional(),
});

function UserPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load user. Please try again.',
        };
      }}
    >
      <UserDetail />
    </InfoPageSuspenseBoundary>
  );
}

function UserDetail() {
  const { userId } = Route.useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(userId),
    queryFn: () => getUser(userId),
  });

  const didCatchError = useCallback(
    (title: string, e: unknown) => {
      const error = airServerErrorFrom(e);
      if (error) {
        toast({
          variant: 'destructive',
          title,
          description: error.userFacingMessage,
        });
      }
    },
    [toast],
  );

  const { mutateAsync: updateUserMutate } = useMutation({
    mutationFn: updateUser,
  });

  const { mutateAsync: updateCoachProfileMutate } = useMutation({
    mutationFn: updateCoachProfile,
  });

  const { mutateAsync: updateStudentProfileMutate } = useMutation({
    mutationFn: updateStudentProfile,
  });

  const onSubmitBasicInfo = ({ formData, disableEditing }: SubmitProps<EditBasicInfoForm>) => {
    return updateUserMutate({
      userId,
      data: {
        ...formData,
        timeZone: formData.timeZone.value,
        edlinkId: ((edlinkId: string | null) => {
          switch (edlinkId) {
            case null: {
              return null;
            }
            // We do not get `undefined` here bc the form schema does not encode that.
            default: {
              // "Blanking out" the ID is interpreted as removing it.
              if (!edlinkId.trim()) {
                return null;
              }
              return edlinkId;
            }
          }
        })(formData.edlinkId),
      },
    })
      .then(() => {
        toast({
          title: 'User updated successfully',
        });
        disableEditing();
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(userId) });
        queryClient.invalidateQueries(getUsers());
      })
      .catch((e) => didCatchError('User update failed', e));
  };

  const onSubmitSsoSuccess = () => {
    toast({
      title: 'User updated successfully',
    });
  };
  const onSubmitSsoFailure = (error: unknown) => {
    didCatchError('User update failed', error);
  };

  const onSubmitCoachProfile = ({ formData, disableEditing }: SubmitProps<z.infer<typeof editCoachProfileSchema>>) => {
    return updateCoachProfileMutate({
      userId,
      data: {
        ...formData,
      },
    })
      .then(() => {
        toast({
          title: 'Teacher updated successfully',
        });
        disableEditing();
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(userId) });
      })
      .catch((e) => didCatchError('Teacher update failed', e));
  };

  const onSubmitStudentProfile = ({
    formData,
    disableEditing,
  }: SubmitProps<z.infer<typeof editStudentProfileSchema>>) => {
    return updateStudentProfileMutate({
      userId,
      data: {
        ...formData,
        grade: formData.grade === 'null' ? null : formData.grade,
      },
    })
      .then(() => {
        toast({
          title: 'Student updated successfully',
        });
        disableEditing();
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(userId) });
      })
      .catch(() => {
        toast({
          title: 'Student update failed',
        });
      });
  };

  return (
    <InfoPage
      headerText={user.fullNameOrEmail}
      headerActions={
        <>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" icon={ChevronDown}>
                <span>Manage permissions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {isCoach(user) && (
                <ManageCoach
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Delete teacher role</DropdownMenuItem>
                  }
                  user={user}
                />
              )}
              {!isCoach(user) && (
                <PromoteCoach
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Promote as teacher</DropdownMenuItem>}
                  user={user}
                />
              )}
              <AirAdminMenuItems user={user} />
              <ManageEdgeCoach
                user={user}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    {isEdgeCoach(user) ? 'Remove Edge tutors' : 'Add Edge tutors'}
                  </DropdownMenuItem>
                }
              />
              {user.studentProfile && (
                <ManageStudent
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}> Manage student profile</DropdownMenuItem>
                  }
                  user={user}
                />
              )}
              {!user.studentProfile && (
                <CreateStudent
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Create student profile</DropdownMenuItem>
                  }
                  fromUser={user}
                />
              )}
              {user.studentProfile && user.edlinkId && (
                <SyncEdlinkModal
                  modalTitle="Sync Edlink student"
                  entityName={user.fullNameOrEmail ?? ''}
                  entityId={user.edlinkId}
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Sync from Edlink</DropdownMenuItem>}
                  syncFn={() => syncStudentFromEdlink.queryFn(user.id)}
                  syncSentryTags={{ feature: 'edlink-sync-student', userId: user.id }}
                  syncErrorMessage="Edlink student sync failed. Please try again or contact support."
                  onSyncSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: syncStudentFromEdlink.queryKeyFn(user.id) });
                    queryClient.invalidateQueries(getUsers());
                  }}
                  dryRunFn={() => dryRunSyncStudentFromEdlink(user.id)}
                  dryRunSentryTags={{ feature: 'edlink-sync-student-dry-run', studentId: user.id }}
                />
              )}
              <DeleteUser
                user={user}
                trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Delete user</DropdownMenuItem>}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    >
      <>
        <EditableInfoCard
          headerText="Basic information"
          formSchema={editBasicInfoFormSchema}
          onSubmit={onSubmitBasicInfo}
          defaultValues={{
            ...user,
            firstName: user.firstName || undefined,
            lastName: user.lastName || undefined,
            email: user.email ?? '',
            timeZone: {
              value: user.timeZone || '',
              label: AllTimeZones.find((tz) => tz.value === user.timeZone)?.label || '',
            },
            edlinkId: user.edlinkId || undefined,
          }}
        >
          {({ form, editing }) => {
            return (
              <>
                <InfoKeyValue
                  label="First name"
                  value={user.firstName}
                  edit={{
                    editing,
                    editModeComponent: <FormInputField name="firstName" label="First Name" form={form} />,
                  }}
                />
                <InfoKeyValue
                  label="Last name"
                  value={user.lastName}
                  edit={{
                    editing,
                    editModeComponent: <FormInputField name="lastName" label="Last Name" form={form} />,
                  }}
                />
                <InfoKeyValue
                  label="Email"
                  value={user.email}
                  edit={{
                    editing,
                    editModeComponent: <FormInputField name="email" label="Email" form={form} />,
                  }}
                />
                <InfoKeyValue
                  label="Phone number"
                  value={user.phone}
                  edit={{
                    editing,
                    editModeComponent: <FormInputField name="phone" label="Phone number" form={form} />,
                  }}
                />
                <InfoKeyValue
                  label="Time zone"
                  value={user.timeZone}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        name="timeZone"
                        label="Time zone"
                        form={form}
                        {...comboBoxOptionsOf(getTimezoneTypeahead)}
                        triggerProps={{ className: 'w-2/3' }}
                        required
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Edlink ID"
                  value={<EdlinkIdCode code={user.edlinkId} />}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        name="edlinkId"
                        label="Edlink ID"
                        form={form}
                        inputProps={{ className: 'font-mono' }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Roles"
                  value={
                    <div className="flex gap-x-1">
                      <RoleBadgeList userRoles={user.userRoles} />
                    </div>
                  }
                />
              </>
            );
          }}
        </EditableInfoCard>
        <SsoEditableInfoCard user={user} onSuccess={onSubmitSsoSuccess} onError={onSubmitSsoFailure} />
        {user.studentProfile && (
          <EditableInfoCard
            headerText="Student information"
            formSchema={editStudentProfileSchema}
            onSubmit={onSubmitStudentProfile}
            defaultValues={{
              grade: user.studentProfile.grade ?? 'null',
              specialEd: user.studentProfile.specialEd || undefined,
            }}
          >
            {({ form, editing }) =>
              user.studentProfile && (
                <>
                  <InfoKeyValue
                    label="School year"
                    value={studentYearOption(user.studentProfile.grade ?? 'null')}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormSingleSelect
                          name="grade"
                          label="Grade"
                          form={form}
                          options={studentYearValues}
                          renderOption={studentYearOption}
                          triggerProps={{ className: 'w-2/3' }}
                        />
                      ),
                    }}
                  />
                  <InfoKeyValue
                    label="School"
                    value={
                      <>
                        {user.userRoles
                          .filter(isStudentRole)
                          .map(({ school }) => school?.name)
                          .join(', ')}
                      </>
                    }
                    edit={{
                      editing,
                      editModeComponent: (
                        <div>
                          <FormLabel className="font-bold">School</FormLabel>
                          <div>To change school, use the Manage permissions dropdown menu</div>
                        </div>
                      ),
                    }}
                  />
                  <InfoKeyValue
                    label="Class time"
                    value={
                      <>
                        {user.studentProfile.requestedClassTime
                          ? formatStudentClassTimeAbbr(user.studentProfile.requestedClassTime)
                          : 'No class time assigned'}
                      </>
                    }
                    edit={{
                      editing,
                      editModeComponent: (
                        <div>
                          <FormLabel className="font-bold">Class time</FormLabel>
                          <div>Please assign class time on the school's student page</div>
                        </div>
                      ),
                    }}
                  />
                  <InfoKeyValue
                    label={'Starting lesson'}
                    value={
                      user.studentProfile.learningPath && user.studentProfile.startingLesson ? (
                        <>
                          {user.studentProfile.learningPath.name} {user.studentProfile.startingLesson.name}
                        </>
                      ) : (
                        'No starting lesson'
                      )
                    }
                    edit={{
                      editing,
                      editModeComponent: (
                        <div>
                          <FormLabel className="font-bold">Starting lesson</FormLabel>
                          <div>Please assign starting lesson on the school's student page</div>
                        </div>
                      ),
                    }}
                  />
                  <InfoKeyValue
                    label="Special Ed"
                    value={user.studentProfile.specialEd ? 'Yes' : 'No'}
                    edit={{
                      editing,
                      editModeComponent: <FormCheckbox name="specialEd" form={form} label={'Special Ed'} />,
                    }}
                  />
                </>
              )
            }
          </EditableInfoCard>
        )}
        {user.coachProfile && (
          <EditableInfoCard
            headerText="Teacher information"
            formSchema={editCoachProfileSchema}
            onSubmit={onSubmitCoachProfile}
            headerActions={
              <>
                <RefreshZoomInfo
                  user={user}
                  trigger={
                    <Button type="button" variant="secondary">
                      Refresh Zoom info
                    </Button>
                  }
                />
              </>
            }
            defaultValues={{
              isTrial: user.coachProfile.isTrial || false,
              qualityGrade: user.coachProfile.qualityGrade || undefined,
              demoGrade: user.coachProfile.demoGrade || undefined,
              behaviorNotes: user.coachProfile.behaviorNotes || undefined,
            }}
          >
            {({ editing, form }) =>
              user.coachProfile && (
                <>
                  <InfoKeyValue
                    label={'Zoom url'}
                    labelTooltip={'Do not update this manually. Use the "Refresh Zoom info" button to update.'}
                    value={user.coachProfile.zoomPersonalMeetingUrl}
                  />
                  <InfoKeyValue label={'Zoom personal meeting Id'} value={user.coachProfile.zoomPersonalMeetingId} />
                  <InfoKeyValue label="Zoom license type" value={zoomTypeToDisplayName(user.coachProfile.zoomType)} />
                  <InfoKeyValue label="Zoom invite status" value={user.coachProfile.zoomInviteStatus} />
                  <InfoKeyValue
                    label="Is trial"
                    value={user.coachProfile.isTrial ? 'Yes' : 'No'}
                    edit={{
                      editing,
                      editModeComponent: <FormCheckbox name="isTrial" label="Is trial" form={form} />,
                    }}
                  />
                  <InfoKeyValue
                    label="Behavior notes"
                    value={user.coachProfile.behaviorNotes}
                    edit={{
                      editing,
                      editModeComponent: <FormTextArea name="behaviorNotes" label="Behavior notes" form={form} />,
                    }}
                  />
                  <InfoKeyValue
                    label="Quality grade"
                    value={user.coachProfile.qualityGrade}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormSingleSelect
                          name="qualityGrade"
                          label="Quality grade"
                          form={form}
                          options={coachGradeOptions}
                        />
                      ),
                    }}
                  />
                  <InfoKeyValue
                    label="Demo grade"
                    value={user.coachProfile.demoGrade}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormSingleSelect name="demoGrade" label="Demo grade" form={form} options={coachGradeOptions} />
                      ),
                    }}
                  />
                </>
              )
            }
          </EditableInfoCard>
        )}
      </>
    </InfoPage>
  );
}
